import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Download, Gamepad2 } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Terms & Conditions', path: "/terms" },
    { name: 'Download', path: '/download' },
    { name: 'Privacy Policy', path: '/privacy' },
  ];

  // Coming Soon handler - shows modal instead of downloading
  const handleComingSoon = () => {
    setShowComingSoon(true);
  };

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0A0F1E]/95 backdrop-blur-lg border-b border-[#1E2338]' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <Gamepad2 className="w-8 h-8 text-[#00fff5]" />
              <span className="text-2xl font-rajdhani font-bold text-white">
                The<span className="text-[#00fff5]">Arena</span>X
              </span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-rajdhani font-semibold text-sm tracking-wider transition-colors ${
                    location.pathname === link.path
                      ? 'text-[#00fff5]'
                      : 'text-gray-300 hover:text-[#00fff5]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <button 
                onClick={handleComingSoon} 
                className="bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-6 py-2 rounded-lg font-rajdhani font-bold text-white flex items-center space-x-2 hover:opacity-90 transition"
              >
                <Download className="w-4 h-4" />
                <span>Download App</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-white p-2"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden bg-[#1E2338] border-t border-[#2A2F45]">
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block font-rajdhani font-semibold py-2 ${
                    location.pathname === link.path
                      ? 'text-[#00fff5]'
                      : 'text-gray-300'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <button 
                onClick={handleComingSoon} 
                className="w-full bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-6 py-3 rounded-lg font-rajdhani font-bold text-white flex items-center justify-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download App</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Coming Soon Modal */}
      {showComingSoon && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[999] p-4">
          <div 
            className="bg-[#1E2338] border border-[#2A2F45] rounded-3xl p-8 max-w-md w-full text-center relative overflow-hidden"
          >
            {/* Background glow */}
            <div className="absolute -top-20 -left-20 w-60 h-60 bg-[#00fff5] rounded-full filter blur-3xl opacity-10"></div>
            <div className="absolute -bottom-20 -right-20 w-60 h-60 bg-[#7000ff] rounded-full filter blur-3xl opacity-10"></div>
            
            <div className="relative z-10">
              <div className="inline-block p-4 bg-[#00fff510] rounded-full mb-4">
                <svg className="w-12 h-12 text-[#00fff5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              
              <h3 className="text-2xl font-rajdhani font-bold text-white mb-2">
                Coming Soon!
              </h3>
              <p className="text-gray-400 font-rajdhani mb-3">
                We're working on something amazing! The app will be available shortly.
              </p>
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="w-2 h-2 bg-[#00fff5] rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-[#00fff5] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-[#00fff5] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
              <button
                onClick={() => setShowComingSoon(false)}
                className="bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-8 py-3 rounded-xl text-white font-rajdhani font-bold hover:opacity-90 transition w-full"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;